/**
 * StressTest — P13. X-month income disruption + rent drop + rate hike sliders.
 * Shows emergency buffer required + wealth impact.
 */
import { useState } from "react";
import { ShieldAlert, ShieldCheck } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, ReferenceLine,
} from "recharts";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { inr } from "../../lib/format";

export default function StressTest({ request }) {
  const [knobs, setKnobs] = useState({
    disruption_months: 6,
    rent_drop_pct: 30,
    rate_hike_pct: 1.0,
    salary_cut_pct: 50,
    disruption_start_month: 12,
  });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!request) {
      toast.error("Run a base simulation first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/snowball/stress-test", { request, ...knobs });
      setRes(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Stress test failed");
    } finally {
      setBusy(false);
    }
  };

  const chartData = res?.stressed?.monthly
    ?.filter((_, i) => i % Math.max(1, Math.floor(res.stressed.monthly.length / 80)) === 0)
    .map((r) => ({ month: r.month, cf: r.portfolio_cf, fund: r.fund })) || [];

  return (
    <div className="card-flat p-5 space-y-4" data-testid="snowball-stress-test">
      <div className="flex items-center gap-2">
        <ShieldAlert size={18} className="text-rose-600" />
        <h4 className="font-serif text-lg">Stress test</h4>
        <span className="text-xs text-muted-foreground">— what if the worst happens?</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <StressSlider label="Disruption duration (months)" value={knobs.disruption_months} onChange={(v) => setKnobs({ ...knobs, disruption_months: v })} min={1} max={36} step={1} suffix="m" testid="stress-duration" />
        <StressSlider label="Salary cut during disruption" value={knobs.salary_cut_pct} onChange={(v) => setKnobs({ ...knobs, salary_cut_pct: v })} min={0} max={100} step={5} suffix="%" testid="stress-salary-cut" />
        <StressSlider label="Rent drop (vacancy spike)" value={knobs.rent_drop_pct} onChange={(v) => setKnobs({ ...knobs, rent_drop_pct: v })} min={0} max={100} step={5} suffix="%" testid="stress-rent-drop" />
        <StressSlider label="Loan rate hike (RBI shock)" value={knobs.rate_hike_pct} onChange={(v) => setKnobs({ ...knobs, rate_hike_pct: v })} min={0} max={5} step={0.25} suffix="pp" testid="stress-rate-hike" />
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          The test layers all shocks: salary cut, vacancy spike and a rate hike — the realistic &quot;bad year&quot; scenario.
        </p>
        <Button onClick={run} disabled={busy} data-testid="stress-test-run">
          {busy ? "Stressing…" : "Run stress test"}
        </Button>
      </div>

      {res && (
        <div className="space-y-3 mt-2" data-testid="stress-test-results">
          <div className={`rounded-md border p-3 ${
            res.portfolio_survives ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"
          }`}>
            <div className="flex items-center gap-2">
              {res.portfolio_survives
                ? <ShieldCheck size={16} className="text-emerald-700" />
                : <ShieldAlert size={16} className="text-rose-700" />}
              <span className={`font-medium ${res.portfolio_survives ? "text-emerald-700" : "text-rose-700"}`}>
                {res.verdict}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StressStat label="Max monthly deficit" value={inr(Math.abs(res.max_monthly_deficit))} accent="bad" />
            <StressStat label="Emergency buffer needed" value={inr(res.emergency_buffer_required)} accent="hero" />
            <StressStat label="Wealth impact" value={`${res.wealth_impact_pct > 0 ? "+" : ""}${res.wealth_impact_pct}%`} accent={res.wealth_impact_pct < 0 ? "bad" : "good"} />
            <StressStat
              label="Stressed sweet spot"
              value={res.stressed.sweet_spot_month != null ? `M${res.stressed.sweet_spot_month}` : "Never"}
              accent={res.stressed.sweet_spot_month != null ? "good" : "bad"}
            />
          </div>

          <div>
            <h5 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Stressed cashflow over time
            </h5>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="month" tickFormatter={(m) => `M${m}`} />
                <YAxis tickFormatter={(v) => v >= 100000 ? `₹${(v / 100000).toFixed(0)}L` : `${v / 1000}k`} />
                <Tooltip formatter={(v) => inr(v)} labelFormatter={(m) => `Month ${m}`} />
                <Legend />
                <ReferenceLine y={0} stroke="#999" strokeDasharray="3 3" />
                <Line type="monotone" dataKey="cf" name="Portfolio CF (stressed)" stroke="#e11d48" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

function StressStat({ label, value, accent }) {
  const color = accent === "good" ? "text-emerald-700" :
                accent === "bad" ? "text-rose-700" :
                accent === "hero" ? "text-[#0B7B6E]" : "text-foreground";
  return (
    <div className="rounded-md border border-[#e5e1d6] bg-white p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-lg font-serif ${color}`}>{value}</div>
    </div>
  );
}

function StressSlider({ label, value, onChange, min, max, step, suffix, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono font-semibold text-rose-700">{value}{suffix}</span>
      </div>
      <input
        type="range" value={value} min={min} max={max} step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-2 bg-[#e5e1d6] rounded-lg appearance-none cursor-pointer accent-rose-600"
      />
    </div>
  );
}
